import {ChangeDetectorRef, Component, Inject, Input, OnInit} from "@angular/core";
import { environment } from '../../../../../environments/environment';
import {WindowRefService} from "../../../../services/window-ref.service";
import {EventBusService} from "../../../../services/event-bus.service";
import {HotelToursSearchService} from "../../../../services/search/hotel-tours-search.service";
import {HotelToursSearchParams} from "../../../../types/interfaces/hotel-tour-search";
import {pluralize} from "../../../pipes/pluralize/pluralize";
import {NgCounterComponent} from "../../../ui/counter/counter.component";
import {NgClass} from "@angular/common";
import {OnDestroy$} from "@pdtec/ngx-observable-lifecycle";
import {Subscription} from "rxjs";
import {filter} from "rxjs/operators";
import {HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig} from "../../../../config/hotel-tours-list-page.config";

@Component({
  selector: 'app-tourists',
  templateUrl: environment.mobile ? './tourists.component.mobile.html' : './tourists.component.html',
  styleUrls: environment.mobile ? ['./tourists.component.mobile.scss'] : ['./tourists.component.scss'],
  imports: [NgCounterComponent, NgClass],
  standalone: true
})
export class TouristsComponent extends OnDestroy$ implements OnInit {
  @Input() initialSearchParams: HotelToursSearchParams | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private cdRef: ChangeDetectorRef,
    private windowService: WindowRefService,
    private eventBusService: EventBusService,
    private searchService: HotelToursSearchService,
    @Inject(HOTEL_TOURS_PAGE_CONFIG) private hotelToursPageConfig: HotelToursPageConfig,
  ) {
    super();
  }

  searchParams: HotelToursSearchParams;
  adults: number;
  isShowAgeSelect: boolean = false;
  isShowAdultsSelect: boolean = false;
  openedChildSelectIndex: number | null = null;
  adultsOptions: number[] = Array.from({length: 9}, (_, i) => i + 1);
  ages: number[] = Array.from({length: 16}, (_, i) => i + 1);
  selectedChildren: number[] = [];
  childAgeFields: (number | null)[] = [null];
  isShowSplitRooms: boolean;
  isSplitRooms: boolean = true;

  ngOnInit() {
    this.searchParams = this.initialSearchParams ?? this.searchService.getLastSearchParams();
    this.adults = this.searchParams.adults;
    this.selectedChildren = [...this.searchParams.childAges];
    this.childAgeFields = this.selectedChildren.length ? [...this.selectedChildren] : [null];

    this.eventBusService.changeModalTitle$.next(this.touristsModalTitle());

    const closeModalSubscription = this.eventBusService.isCloseModal$
      .pipe(
        filter((modal: HTMLElement) => modal.classList.contains('tourists-modal'))
      )
      .subscribe(() => {
        this.searchParams = this.initialSearchParams ?? this.searchService.getLastSearchParams();
        this.adults = this.searchParams.adults;
        this.selectedChildren = [...this.searchParams.childAges];
        this.childAgeFields = this.selectedChildren.length ? [...this.selectedChildren] : [null];
        this.openedChildSelectIndex = null;

        this.eventBusService.changeModalTitle$.next(this.touristsModalTitle());
      });

    this.subscriptions.push(closeModalSubscription);

    this.splitRoomsDetector(true);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onChangeAdults(value) {
    this.adults = value;
    this.eventBusService.changeModalTitle$.next(this.touristsModalTitle());

    this.eventBusService.shortSearchParams$.next({
      adults: value
    });
    this.splitRoomsDetector();
  }

  toggleAdultsSelect() {
    this.isShowAdultsSelect = !this.isShowAdultsSelect;
    if (this.isShowAdultsSelect) {
      this.openedChildSelectIndex = null;
    }
  }

  getAdultsLabel() {
    return this.adults + ' ' + pluralize(this.adults, ['взрослый', 'взрослых', 'взрослых']);
  }

  selectAdults(value: number) {
    this.onChangeAdults(value);
    this.isShowAdultsSelect = false;
  }

  toggleChildSelect(index: number) {
    this.openedChildSelectIndex = this.openedChildSelectIndex === index ? null : index;
    if (this.openedChildSelectIndex !== null) {
      this.isShowAdultsSelect = false;
    }
  }

  selectChildAge(index: number, age: number) {
    this.childAgeFields[index] = age;
    this.openedChildSelectIndex = null;
    this.syncChildrenFromFields();
  }

  addChildField() {
    if (this.childAgeFields.length >= 4) {
      return;
    }
    this.childAgeFields.push(null);
    this.openedChildSelectIndex = this.childAgeFields.length - 1;
  }

  removeChildField(index: number) {
    if (this.childAgeFields.length === 1) {
      this.childAgeFields[0] = null;
    } else {
      this.childAgeFields.splice(index, 1);
    }

    if (this.openedChildSelectIndex !== null) {
      if (this.openedChildSelectIndex === index) {
        this.openedChildSelectIndex = null;
      } else if (this.openedChildSelectIndex > index) {
        this.openedChildSelectIndex--;
      }
    }

    this.syncChildrenFromFields();
  }

  removeSelectedChild(index: number) {
    if (index < 0 || index >= this.selectedChildren.length) {
      return;
    }

    this.selectedChildren.splice(index, 1);
    this.childAgeFields = this.selectedChildren.length ? [...this.selectedChildren] : [null];
    this.syncChildrenState();
  }

  getChildAgeLabel(index: number) {
    const age = this.childAgeFields[index];
    if (age === null || age === undefined) {
      return 'выберите';
    }
    return this.getAgeLabel(age);
  }

  private syncChildrenFromFields() {
    this.selectedChildren = this.childAgeFields.filter((age): age is number => age !== null);
    this.syncChildrenState();
  }

  private syncChildrenState() {
    this.eventBusService.shortSearchParams$.next({
      childAges: this.selectedChildren
    });
    this.splitRoomsDetector();
    this.eventBusService.changeModalTitle$.next(this.touristsModalTitle());
  }

  private touristsModalTitle() {
    let label = this.adults + ' ' + pluralize(this.adults, ['взрослый', 'взрослых', 'взрослых']);
    const childrenCount = this.selectedChildren.length;
    if (childrenCount > 0) {
      label += ', ' + childrenCount + ' ' + pluralize(childrenCount, ['ребенок', 'ребенка', 'детей']);
    }
    const title = '<div class="tourists-modal__title">' + label + '</div>';
    const subtitle = '<div class="tourists-modal__subtitle">Кто летит</div>';

    return subtitle + title;
  }

  addChildren() {
    this.isShowAgeSelect = true;
  }

  hideAgeSelect() {
    this.isShowAgeSelect = false;
  }

  selectAge(age) {
    this.hideAgeSelect();
    this.selectedChildren.push(age);
    this.childAgeFields = this.selectedChildren.length ? [...this.selectedChildren] : [null];
    this.syncChildrenState();
  }

  getAgeLabel(age) {
    if (age === 0) {
      return 'До года';
    }

    return age + ' ' + pluralize(age, ['год', 'года', 'лет'])
  }

  changeSplitRoomsCheckbox(event) {
    const checkbox = event.target as HTMLInputElement;
    this.isSplitRooms = checkbox.checked;

    this.toggleSplitRooms(checkbox.checked);
  }

  toggleSplitRooms(value: boolean) {
    this.eventBusService.shortSearchParams$.next({
      splitRooms: value
    });
  }

  splitRoomsDetector(isInit: boolean = false) {
    const childrenCount = this.selectedChildren.length;
    this.isShowSplitRooms = this.adults > 3 || (this.adults > 1 && (this.adults + childrenCount) > 4) ||
      (this.adults > 2 && childrenCount > 0);

    if (isInit) {
      if (this.searchParams.splitRooms) {
        this.toggleSplitRooms(true);
      } else if (this.isShowSplitRooms) {
        this.isSplitRooms = false;
      }
    } else if (!this.isShowSplitRooms) {
      this.toggleSplitRooms(false);
    } else {
      if (this.isShowSplitRooms && this.isSplitRooms === true) {
        this.toggleSplitRooms(true);
      }
    }
  }
}
